import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Sparkles, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { registerEarlyAccess } from "@/lib/api";
import { CATEGORIES } from "@/data/campaigns";

/**
 * Early Access signup — used on the Home page and (optionally) Categories page.
 * Persists to /api/early-access on the backend. Idempotent by email.
 *
 * Props:
 *  - defaultCategory: pre-select a category (e.g. from a category card click)
 *  - source: analytics source tag ("home" | "categories" | "cta" ...)
 *  - compact: render a smaller inline variant
 */
const EarlyAccessForm = ({ defaultCategory = "", source = "home", compact = false }) => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [categoryInterest, setCategoryInterest] = useState(defaultCategory);
  const [role, setRole] = useState("creator");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter your email");
      return;
    }
    setSubmitting(true);
    try {
      const res = await registerEarlyAccess({
        email: email.trim(),
        name: name.trim() || null,
        category_interest: categoryInterest || null,
        role,
        source,
      });
      setDone(true);
      if (res?.already_registered) {
        toast.success("You're already on the list — we'll be in touch soon.");
      } else {
        toast.success("You're in. Watch your inbox for early access.");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="early-access-success"
        className="rounded-2xl border border-forest-500/40 bg-forest-500/10 p-6 flex items-start gap-3"
      >
        <CheckCircle2 className="h-5 w-5 text-forest-700 flex-shrink-0 mt-0.5" />
        <div>
          <div className="font-display font-bold text-navy-800">You're on the list.</div>
          <p className="text-sm text-navy-600 mt-1 leading-relaxed">
            We'll email <span className="font-semibold text-navy-800">{email}</span> the moment early access opens. In the
            meantime, follow along on our socials for behind-the-scenes updates.
          </p>
        </div>
      </div>
    );
  }

  const inputCls =
    "w-full h-12 rounded-2xl border border-warm-200 bg-white px-4 text-sm text-navy-800 placeholder:text-navy-400 focus:outline-none focus:border-saffron-500 focus:ring-4 focus:ring-saffron-500/15 transition";

  return (
    <form
      onSubmit={onSubmit}
      data-testid="early-access-form"
      className={`space-y-3 ${compact ? "" : "sm:space-y-4"}`}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input
          type="text"
          placeholder="Full name (optional)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          data-testid="early-access-name"
          className={inputCls}
        />
        <input
          type="email"
          required
          placeholder="you@work.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="early-access-email"
          className={inputCls}
        />
      </div>

      {!compact && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <select
            value={categoryInterest}
            onChange={(e) => setCategoryInterest(e.target.value)}
            data-testid="early-access-category"
            className={inputCls + " appearance-none pr-10"}
          >
            <option value="">Interested in… (optional)</option>
            {CATEGORIES.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>

          <div className="grid grid-cols-2 rounded-2xl border border-warm-200 bg-white p-1">
            {[
              { v: "creator", label: "I'm a Creator" },
              { v: "backer", label: "I'm a Backer" },
            ].map((r) => (
              <button
                key={r.v}
                type="button"
                onClick={() => setRole(r.v)}
                data-testid={`early-access-role-${r.v}`}
                className={`h-10 rounded-xl text-sm font-semibold transition-colors ${
                  role === r.v ? "bg-navy-800 text-white shadow-navy-soft" : "text-navy-600 hover:text-navy-800"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <Button
        type="submit"
        disabled={submitting}
        data-testid="early-access-submit"
        className="btn-saffron w-full sm:w-auto rounded-2xl h-12 px-6 font-semibold disabled:opacity-70"
      >
        {submitting ? "Reserving your spot…" : (
          <>
            Join Early Access <ArrowRight className="ml-2 h-4 w-4" />
          </>
        )}
      </Button>

      <p className="text-xs text-navy-500 flex items-center gap-1.5">
        <Sparkles className="h-3.5 w-3.5 text-saffron-600" />
        No spam. We'll only email you when early access is ready.
      </p>
    </form>
  );
};

export default EarlyAccessForm;
